// @givmo/auth — passkey SIGN-IN (AS-WEB2).
//
// A WebAuthn assertion, exchanged at the backend for a Firebase custom token:
//
//   POST /authn/passkeys/assertion/options  →  navigator.credentials.get()  →
//   POST /authn/passkeys/assertion          →  { custom_token }             →  signInWithCustomToken
//
// SIGN-IN ONLY — never a step-up. `signInWithCustomToken` ESTABLISHES a session, and establishing one
// is exactly what a step-up must not do: whoever asserts becomes the signed-in user, so a passkey arm
// on a re-auth dialog would let a different account finish the money action the dialog was gating.
// That is the failure `reauth.ts` was written to remove from super-admin's money re-auth (AS-WEB3);
// re-introducing it here as a "convenience" would restore it. Step-up stays on `reauthenticate*`,
// which binds to the `User` it was handed. See `agent_docs/auth.md`.
//
// No uid binding is needed HERE, for the mirror-image reason: establishing identity IS the operation,
// so any account that can produce a valid assertion is a legitimate outcome. The backend issues
// options for DISCOVERABLE credentials with NO `allowCredentials`, so the ceremony neither takes nor
// leaks a claim about which accounts exist.
//
// ⚠ THE CUSTOM TOKEN IS A BEARER CREDENTIAL. It is held only as a local const between the transport
// call and `signInWithCustomToken`, and is never stored on an object, attached to an error, or passed
// to anything that could serialise it — the same rule the `MultiFactorError` WeakMap in `errors.ts`
// enforces for `mfaPendingCredential`, reached here by giving the value no lifetime rather than by
// hiding it. `passkeys.test.ts` pins it against both `JSON.stringify(err)` and the SPREAD form,
// because a value on an own enumerable property is invisible to the first and caught only by the second.
//
// NO second-factor branch, deliberately. GCIP does not raise `auth/multi-factor-auth-required` on a
// custom-token sign-in: the token asserts an identity the BACKEND already verified, and the ID token
// it mints carries `amr: ["passkey_uv"]` recording that a user-verifying ceremony took place. Adding a
// `withSecondFactorChallenge` wrapper here would be unreachable code claiming a capability.
//
// Registration is a separate module (`passkey-registration.ts`, AS-WEB11): the signed-in account
// adds a passkey on the web or in the Givmo app, and then signs in with it here.

import { signInWithCustomToken, type Auth, type UserCredential } from 'firebase/auth';
import { AuthError } from './errors';
import {
  isWebAuthnSupported,
  serializeAssertion,
  toRequestOptions,
  type AssertionCredentialLike,
  type PasskeyAssertionJson,
  type WebAuthnScope,
} from './webauthn';

/** The backend's machine `error_code` for any failed assertion — deliberately uniform. */
export const PASSKEY_CEREMONY_FAILED = 'PASSKEY_CEREMONY_FAILED';

/**
 * The one refusal the backend separates out of the uniform failure above, because it has a remedy the
 * user can act on: the assertion arrived without proof that the authenticator verified the person
 * (no biometric/PIN check), which this deployment requires.
 */
export const PASSKEY_USER_VERIFICATION_REQUIRED = 'PASSKEY_USER_VERIFICATION_REQUIRED';

/**
 * The backend's machine `error_code` when the company's sign-in lock refuses the passkey mint
 * (`CompanySsoRequiredException`, AS-BE15): custom-token sign-ins never reach the blocking function,
 * so the mint is the lock's only gate here. The same refusal as the blocking function's, read as the
 * same kind (`organizationSsoRequired`, AS-WEB27), so the door shows the same notice.
 */
export const COMPANY_SSO_REQUIRED = 'COMPANY_SSO_REQUIRED';

/** Shown when the browser cannot run the ceremony at all (no `PublicKeyCredential` / no container). */
const PASSKEYS_UNAVAILABLE_MESSAGE = 'Passkeys are not available in this browser.';

/**
 * The transport the assertion rides on, injected as a port exactly like {@link AdminAuthBackend} —
 * this package stays free of any HTTP coupling, and the app supplies the adapter. Both routes live on
 * the backend's UNAUTHENTICATED `/authn` mount and take no Authorization header: the whole point is
 * that nobody is signed in yet.
 */
export interface PasskeyAssertionTransport {
  /**
   * `POST /authn/passkeys/assertion/options` → the raw `PublicKeyCredentialRequestOptionsJSON`.
   *
   * Each call issues a FRESH single-use challenge with its own short TTL, so a caller must never
   * cache one across a user gesture — {@link probePasskeySignIn} discards the document it gets for
   * exactly that reason.
   */
  requestAssertionOptions(init?: { signal?: AbortSignal }): Promise<unknown>;
  /**
   * `POST /authn/passkeys/assertion` → the Firebase custom token.
   *
   * Rejects with a {@link PasskeyTransportError} carrying the backend's machine `error_code` and
   * status. An implementation must build that error from the ERROR ENVELOPE only — never from the
   * success body, which carries the bearer token.
   */
  exchangeAssertion(
    assertion: PasskeyAssertionJson,
    init?: { signal?: AbortSignal },
  ): Promise<string>;
}

/**
 * A structured refusal from the assertion transport. The adapter builds it from the response error
 * envelope so this package can branch on the backend's machine `error_code` — never on message text,
 * which is English copy the backend is free to reword.
 */
export class PasskeyTransportError extends Error {
  /** The backend's `error_code`, or `null` when the refusal carried none. */
  readonly errorCode: string | null;
  /** The HTTP status, or `null` on a transport failure that produced no response. */
  readonly status: number | null;

  constructor(args: { message: string; errorCode?: string | null; status?: number | null }) {
    super(args.message);
    this.name = 'PasskeyTransportError';
    this.errorCode = args.errorCode ?? null;
    this.status = args.status ?? null;
    Object.setPrototypeOf(this, PasskeyTransportError.prototype);
  }
}

/**
 * The verdict {@link probePasskeySignIn} returns. Four states rather than a boolean because the
 * CALLER has to cache them differently — the backend's per-IP options budget is small and shared with
 * real sign-ins, so how long an answer may be trusted depends on why it was given:
 *
 * - `available`   — the routes answered. Cannot become false without a deploy.
 * - `unavailable` — the routes 404 (the backend feature is dark) or answered unusably.
 * - `unsupported` — this browser cannot run a ceremony. No request was spent reaching this.
 * - `rateLimited` — a 429. Says nothing about whether the feature exists, so it must NOT be cached
 *                   as though it were `unavailable`; it is a "come back shortly" answer.
 */
export type PasskeyAvailability = 'available' | 'unavailable' | 'unsupported' | 'rateLimited';

/** Injectable seams: the WebAuthn entry point, the global probed for support, and a cancel signal. */
export interface PasskeySignInOptions {
  /** Defaults to `navigator.credentials`. Injected in tests — jsdom ships no credentials container. */
  credentials?: Pick<CredentialsContainer, 'get'>;
  /** Defaults to `globalThis`. Probed for `PublicKeyCredential` + its JSON converters. */
  scope?: WebAuthnScope;
  /** Aborts the ceremony (e.g. the screen unmounted). Surfaces as `cancelled`, like a dismissal. */
  signal?: AbortSignal;
}

/**
 * Whether a passkey sign-in can be ATTEMPTED here — a pure browser-capability check, with no network
 * call and no claim that the backend serves the routes. Every affordance is gated on this first so an
 * unsupporting browser never renders a control it cannot honour; whether the SERVER side is live is a
 * separate question, answered by {@link probePasskeySignIn}.
 */
export function isPasskeySignInSupported(scope: WebAuthnScope = globalThis): boolean {
  return isWebAuthnSupported(scope);
}

/**
 * Whether passkey sign-in is available END TO END right now: the browser can run a ceremony AND the
 * backend serves the assertion routes.
 *
 * This is the dark-gating mechanism. The routes 404 while the backend's passkey flag is off or its RP
 * id is unset — which is every environment today — so a UI that renders only on `available` cannot
 * show a dead-end control, and it needs no client-side flag to stay in step with the backend (one
 * switch, server-side, with nothing to keep aligned).
 *
 * **Never throws, and is silent on every path.** No console output, no telemetry: while the feature
 * is dark the 404 is the EXPECTED answer, and a surface that logged it would put a line in every
 * admin's console on every visit to the sign-in screen.
 *
 * **The probe's challenge is discarded.** It is single-use with a short TTL, so reusing it at click
 * time — potentially minutes later, after a page the user left open — would fail the ceremony for a
 * reason no one could see. {@link signInWithPasskey} always fetches its own.
 *
 * ⚠ **This request is not free, and the budget is shared with real sign-ins.** The backend guards the
 * options route per IP, fail-closed, with `AUTHN_PASSKEY_ASSERTION_IP_RATE_LIMIT` /
 * `_WINDOW_SECONDS` (AS-BE42: a per-environment operator knob, Terraform-wired; 20 per hour by
 * default) on the same `authn_passkey_assertion_options_ip` class a genuine sign-in consumes.
 * Callers MUST cache this verdict rather than probing per page load — see the caching hook beside
 * the sign-in button, and the reason each of the four states caches differently on
 * {@link PasskeyAvailability}.
 */
export async function probePasskeySignIn(
  transport: PasskeyAssertionTransport,
  options: Pick<PasskeySignInOptions, 'scope' | 'signal'> = {},
): Promise<PasskeyAvailability> {
  if (!isPasskeySignInSupported(options.scope ?? globalThis)) return 'unsupported';
  try {
    const document = await transport.requestAssertionOptions({ signal: options.signal });
    // A 200 whose body carries no challenge is not a usable route; treat it as unavailable rather
    // than rendering a control that would fail at the first click.
    const usable =
      typeof document === 'object' &&
      document !== null &&
      typeof (document as { challenge?: unknown }).challenge === 'string' &&
      (document as { challenge: string }).challenge.length > 0;
    return usable ? 'available' : 'unavailable';
  } catch (error) {
    // A 429 is the one refusal that says nothing about whether the feature EXISTS — reading it as
    // `unavailable` would let a burst of traffic cache the button away for the full negative TTL.
    return error instanceof PasskeyTransportError && error.status === 429
      ? 'rateLimited'
      : 'unavailable';
  }
}

/**
 * Sign in with a passkey, resolving to the Firebase credential.
 *
 * Rejects with an {@link AuthError}: `cancelled` (empty message — surfaces nothing) when the user
 * dismisses the sheet or the ceremony is aborted, `passkeyUserVerificationRequired` when the
 * authenticator did not verify the person, `passkeyCeremonyFailed` for every other ceremony or
 * assertion failure, and `tooManyRequests` on a rate limit.
 *
 * The failure never signs anyone out and never touches the other sign-in paths' state — a passkey
 * attempt that fails leaves the email and provider affordances exactly as they were.
 */
export async function signInWithPasskey(
  auth: Auth,
  transport: PasskeyAssertionTransport,
  options: PasskeySignInOptions = {},
): Promise<UserCredential> {
  const scope = options.scope ?? globalThis;
  const credentials = options.credentials ?? globalThis.navigator?.credentials;
  if (!isPasskeySignInSupported(scope) || credentials === undefined) {
    throw new AuthError('passkeyCeremonyFailed', { message: PASSKEYS_UNAVAILABLE_MESSAGE });
  }

  // A FRESH challenge per attempt — see `probePasskeySignIn` on why the probe's is never reused.
  let requestOptions: PublicKeyCredentialRequestOptions;
  try {
    const document = await transport.requestAssertionOptions({ signal: options.signal });
    requestOptions = toRequestOptions(document, scope);
  } catch (error) {
    throw assertionFailure(error);
  }

  let credential: AssertionCredentialLike | null;
  try {
    credential = (await credentials.get({
      publicKey: requestOptions,
      signal: options.signal,
    })) as AssertionCredentialLike | null;
  } catch (error) {
    throw ceremonyFailure(error);
  }
  if (credential === null) {
    // Spec-permitted "no credential was produced"; indistinguishable from a dismissal, and treated
    // as one so a user who backed out is not shown an error they did not cause.
    throw new AuthError('cancelled');
  }

  // ⚠ The custom token exists only between these two statements. Do not hoist it, store it, log it,
  // or include it in an error — see the module header.
  let userCredential: UserCredential;
  try {
    const customToken = await transport.exchangeAssertion(serializeAssertion(credential), {
      signal: options.signal,
    });
    userCredential = await signInWithCustomToken(auth, customToken);
  } catch (error) {
    throw assertionFailure(error);
  }
  return userCredential;
}

/**
 * Map a transport / custom-token failure onto the error model, branching on the backend's machine
 * `error_code` and status — never on message text (the AS-WEB3 precedent).
 *
 * Anything unrecognised falls through to `AuthError.from`, which keeps a Firebase `auth/*` code
 * verbatim and otherwise retains the server's own (render-safe) sentence.
 */
function assertionFailure(error: unknown): AuthError {
  if (error instanceof PasskeyTransportError) {
    if (error.errorCode === PASSKEY_USER_VERIFICATION_REQUIRED) {
      return new AuthError('passkeyUserVerificationRequired', { code: error.errorCode });
    }
    if (error.errorCode === PASSKEY_CEREMONY_FAILED) {
      return new AuthError('passkeyCeremonyFailed', { code: error.errorCode });
    }
    if (error.errorCode === COMPANY_SSO_REQUIRED) {
      return new AuthError('organizationSsoRequired', { code: error.errorCode });
    }
    if (error.status === 429) {
      return new AuthError('tooManyRequests', { code: error.errorCode ?? undefined });
    }
  }
  return AuthError.from(error);
}

/**
 * Map a `navigator.credentials.get()` rejection onto the error model.
 *
 * `NotAllowedError` is WebAuthn's deliberately ambiguous refusal: a dismissed sheet, a timeout, and
 * an authenticator that declined all arrive as the same name, precisely so a site cannot learn which.
 * Dismissal is overwhelmingly the real case, so it is read as `cancelled` — an empty message that
 * surfaces nothing — rather than accusing a user who simply changed their mind of a failure.
 * Everything else (a `SecurityError` from an origin the RP id does not cover, an unsupported
 * transport) is a genuine failure with the honest generic remedy.
 */
function ceremonyFailure(error: unknown): AuthError {
  const name =
    typeof error === 'object' && error !== null ? (error as { name?: unknown }).name : '';
  if (name === 'NotAllowedError' || name === 'AbortError') {
    return new AuthError('cancelled');
  }
  return new AuthError('passkeyCeremonyFailed');
}
